import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Smartphone, Tablet, Laptop, Gamepad2, Wrench, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const RepairServicesPage = () => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all');

    useEffect(() => {
        const fetchServices = async () => {
            try {
                const response = await axios.get(`${API}/api/repair-services`);
                setServices(response.data);
            } catch {
                toast.error("Errore nel caricamento dei servizi");
            } finally {
                setLoading(false);
            }
        };
        fetchServices();
    }, []);

    const deviceTypes = [
        { id: 'all', label: 'Tutti', icon: Wrench },
        { id: 'smartphone', label: 'Smartphone', icon: Smartphone },
        { id: 'tablet', label: 'Tablet', icon: Tablet },
        { id: 'laptop', label: 'Computer', icon: Laptop },
        { id: 'console', label: 'Console', icon: Gamepad2 },
    ];

    const filteredServices = activeTab === 'all' 
        ? services 
        : services.filter(s => s.device_type === activeTab);

    const getDeviceIcon = (type) => {
        const icons = {
            smartphone: Smartphone,
            tablet: Tablet,
            laptop: Laptop,
            console: Gamepad2,
        };
        return icons[type] || Wrench;
    };

    return (
        <div className="min-h-screen py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-3xl sm:text-4xl font-bold mb-4">Servizi di Riparazione</h1>
                    <p className="text-muted max-w-2xl mx-auto">
                        Riparazioni professionali per tutti i tuoi dispositivi elettronici. 
                        Tecnici certificati e garanzia su ogni intervento.
                    </p>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
                    <TabsList className="bg-surface border border-border p-1 flex flex-wrap justify-center gap-1">
                        {deviceTypes.map((type) => (
                            <TabsTrigger 
                                key={type.id} 
                                value={type.id}
                                className="data-[state=active]:bg-primary data-[state=active]:text-white px-4 py-2"
                                data-testid={`tab-${type.id}`}
                            >
                                <type.icon className="w-4 h-4 mr-2" />
                                {type.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>

                {/* Services Grid */}
                {loading ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div key={i} className="bg-surface rounded-xl h-48 animate-pulse" />
                        ))}
                    </div>
                ) : filteredServices.length === 0 ? (
                    <div className="text-center py-16">
                        <p className="text-muted text-lg">Nessun servizio trovato</p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredServices.map((service) => {
                            const Icon = getDeviceIcon(service.device_type);
                            return (
                                <div
                                    key={service.id}
                                    className="service-card"
                                    data-testid={`service-${service.id}`}
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <Icon className="w-6 h-6 text-primary" />
                                        </div>
                                        <span className="text-2xl font-bold text-primary">
                                            €{service.price.toFixed(2)}
                                        </span>
                                    </div>
                                    <h3 className="font-semibold text-lg mb-2">{service.name}</h3>
                                    <p className="text-muted text-sm mb-4">{service.description}</p>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted">
                                            Durata: ~{service.duration_minutes} min
                                        </span>
                                        <Link to={`/prenota?service=${service.id}`}>
                                            <Button size="sm" className="bg-primary hover:bg-primary-hover">
                                                Prenota
                                                <ArrowRight className="w-4 h-4 ml-1" />
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* CTA */}
                <div className="mt-16 text-center">
                    <div className="bg-gradient-to-r from-primary/20 to-secondary/20 rounded-2xl p-8 border border-primary/30">
                        <h2 className="text-2xl font-bold mb-4">Non trovi il servizio che cerchi?</h2>
                        <p className="text-muted mb-6">
                            Contattaci per una diagnosi gratuita del tuo dispositivo
                        </p>
                        <Link to="/prenota">
                            <Button className="btn-primary">
                                Prenota una diagnosi
                                <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RepairServicesPage;
